import { useMemo, type CSSProperties } from 'react';
import type { Block, ContentPageBlockContent } from '@/types';
import { isLightPageStyle, resolvePageStyleConfig, type PageStyle } from '@/theme/uboTheme';
import { buildSectionCssForStyle } from '@/theme/sectionStyles';
import PageHeader from './PageHeader';
import BrandingImage from '@/components/BrandingImage';
import { useQuoteClientLogo, useQuotePageBrand, useResolvedAgencyLogoUrl } from '@/store/quoteClientLogoContext';
import { contentAreaHeightPxForBrand } from '@/constants/a4';
import { A4_LANDSCAPE } from '@/constants/pageFormat';

interface ContentPageBlockProps {
  block: Block;
  /** Publieke offerte: geen overflow-hidden zodat lange tekst op één kaart volledig zichtbaar is. */
  isPublic?: boolean;
  isContinuation?: boolean;
}

export function buildContentPageCss(blockId: string, style: string, brand: 'ubo' | 'ubx' = 'ubo'): string {
  return buildSectionCssForStyle(`.content-readonly-${blockId}`, style as PageStyle, brand);
}

function ContentPageBlock({ block, isPublic = false, isContinuation = false }: ContentPageBlockProps) {
  const content = block.content as ContentPageBlockContent;
  const quoteClientLogo = useQuoteClientLogo();
  const quoteBrand = useQuotePageBrand();
  const effectiveTheme = content.theme ?? quoteBrand;
  const agencyUrl = useResolvedAgencyLogoUrl(content.agencyLogoUrl, content.theme);
  const style = content.style || 'default';
  const pageBrand = effectiveTheme === 'ubx' ? 'ubx' : 'ubo';
  const styleConfig = resolvePageStyleConfig(style as PageStyle, pageBrand);
  const cssText = useMemo(() => buildContentPageCss(block.id, style, pageBrand), [block.id, style, pageBrand]);
  const contentBodyHeight = contentAreaHeightPxForBrand(pageBrand);

  return (
    <div
      className={`w-full h-full flex-1 relative flex flex-col ${isPublic ? 'overflow-visible' : 'overflow-hidden'}`}
      data-print-continuation={isContinuation ? 'true' : undefined}
      style={{
        backgroundColor: styleConfig.bgColor,
        color: styleConfig.textColor,
        padding: '50px',
        boxSizing: 'border-box',
        minHeight: A4_LANDSCAPE.contentHeight,
        maxWidth: A4_LANDSCAPE.width,
        width: '100%',
        /* Publieke offerte: page-break alleen op outer print-wrapper (voorkomt dubbele break + lege PDF-pagina). */
        pageBreakAfter: isPublic ? 'auto' : 'always',
      }}
    >
      <PageHeader
        clientLogoUrl={content.clientLogoUrl ?? quoteClientLogo}
        agencyLogoUrl={agencyUrl}
        brand={effectiveTheme === 'ubx' ? 'ubx' : 'ubo'}
        variant={isLightPageStyle(style) ? 'light' : 'dark'}
      />
      <BrandingImage style={style} />
      <div
        className={`relative z-[5] flex-1 content-readonly-${block.id}`}
        style={{
          marginTop: isContinuation ? 32 : 24,
          '--quote-content-page-body-height': `${contentBodyHeight}px`,
        } as CSSProperties}
        dangerouslySetInnerHTML={{ __html: content.content || '' }}
      />
      <style>{cssText}</style>
    </div>
  );
}

export default ContentPageBlock;
